// Import Firestore services
import { vehiclesService, invoicesService, garageInfoService } from '../services/firestoreService';
import { auth } from '../firebase/config';
import { formatDateOnlyDisplay } from './helpers';
import { ALL_COMPANY_STAGES, normalizeEnabledStages } from './enabledStages';

// Check if user is authenticated in Firebase
const isFirebaseAuthenticated = () => {
  return auth.currentUser !== null;
};

// Always use Firestore - no localStorage fallback
export const shouldUseFirestore = () => {
  return isFirebaseAuthenticated();
};

// Default vehicle data - empty array (no demo data)
export const DEFAULT_VEHICLES = [];

// Default garage info - empty values (no demo data)
export const DEFAULT_GARAGE_INFO = {
  companyName: '',
  address: '',
  contact: '',
  vatNumber: '',
  siteNames: ['Site A', 'Site B', 'Site C', 'Main Site', 'Storage', 'Other'],
  technicians: [],
  jobCardTemplate: { templateId: 'classic', customLabels: {} },
  branding: null,
  /** When true, daily Cloud Function may send Meta template WhatsApp for upcoming nextServiceDue (if configured server-side). */
  whatsappServiceDueRemindersEnabled: false,
  whatsappMotRemindersEnabled: false,
  enabledStages: [...ALL_COMPANY_STAGES],
};

// Load vehicles from Firestore only - no localStorage fallback
export const loadVehicles = async () => {
  try {
    if (!shouldUseFirestore()) {
      console.warn('User not authenticated, cannot load vehicles from Firestore');
      return [];
    }
    
    const vehicles = await vehiclesService.getAll();
    // Keep id and deletedAt for soft-delete/restore; drop server timestamps for compatibility
    return vehicles.map(({ createdAt, updatedAt, ...rest }) => rest);
  } catch (err) {
    console.error('Error loading vehicles from Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Synchronous version for backward compatibility (returns empty array - use async version)
export const loadVehiclesSync = () => {
  // Always return empty array - use async loadVehicles() for Firestore data
  return [];
};

/**
 * Subscribe to vehicles in real time (Firestore onSnapshot).
 * @param {(vehicles: Array) => void} onUpdate
 * @param {(err: Error) => void} onError
 * @returns {() => void} unsubscribe
 */
export const subscribeVehicles = (onUpdate, onError) => {
  if (!shouldUseFirestore()) return () => {};
  const strip = (list) => list.map(({ createdAt, updatedAt, ...rest }) => rest);
  return vehiclesService.subscribeAll((data) => onUpdate(strip(data)), onError);
};

/**
 * Subscribe to recent invoices in real time (Firestore onSnapshot).
 * @param {(invoices: Array) => void} onUpdate
 * @param {(err: Error) => void} onError
 * @returns {() => void} unsubscribe
 */
export const subscribeInvoices = (onUpdate, onError) => {
  if (!shouldUseFirestore()) return () => {};
  return invoicesService.subscribeRecent(
    (data) => onUpdate(data.map(({ id, createdAt, updatedAt, ...inv }) => inv)),
    onError
  );
};

/**
 * Maps a Firestore garageInfo document to the same shape as loadGarageContact (including optional branding).
 */
export const mapGarageInfoFromFirestore = (info) => {
  if (!info) return null;
  const jobCardTemplate =
    info.jobCardTemplate && typeof info.jobCardTemplate === 'object'
      ? {
          templateId: info.jobCardTemplate.templateId || 'classic',
          customLabels:
            info.jobCardTemplate.customLabels && typeof info.jobCardTemplate.customLabels === 'object'
              ? info.jobCardTemplate.customLabels
              : {},
        }
      : DEFAULT_GARAGE_INFO.jobCardTemplate;

  let branding = null;
  if (info.branding && typeof info.branding === 'object') {
    branding = {
      primaryColor: info.branding.primaryColor,
      secondaryColor: info.branding.secondaryColor,
      fontColor: info.branding.fontColor != null ? info.branding.fontColor : '',
      appearanceTheme: info.branding.appearanceTheme === 'dark' ? 'dark' : 'light',
    };
  }

  return {
    companyName: info.companyName || '',
    address: info.address,
    phone: info.contact,
    email: info.email || '',
    vatNumber: info.vatNumber || '',
    siteNames: info.siteNames || DEFAULT_GARAGE_INFO.siteNames,
    technicians: Array.isArray(info.technicians) ? info.technicians : [],
    jobCardTemplate,
    defaultTechnician: info.defaultTechnician || '',
    defaultSite: info.defaultSite || '',
    branding,
    whatsappServiceDueRemindersEnabled: info.whatsappServiceDueRemindersEnabled === true,
    whatsappMotRemindersEnabled: info.whatsappMotRemindersEnabled === true,
    enabledStages: normalizeEnabledStages(info.enabledStages),
  };
};

/** Subscribe to the company garageInfo document (company settings, shared theme). */
export const subscribeGarageInfo = (onUpdate, onError) => {
  if (!shouldUseFirestore()) return () => {};
  return garageInfoService.subscribe(
    (raw) => {
      onUpdate(mapGarageInfoFromFirestore(raw));
    },
    onError
  );
};

// Save vehicles to Firestore only - no localStorage fallback
// Uses Firestore id for matching (not reg) so duplicate registrations are supported.
export const saveVehicles = async (vehicles) => {
  if (!shouldUseFirestore()) {
    throw new Error('User not authenticated, cannot save vehicles to Firestore');
  }
  
  try {
    const existingVehicles = await vehiclesService.getAll();
    const existingById = new Map(existingVehicles.map(v => [v.id, v]));
    await vehiclesService.batchSave(vehicles, existingById);
  } catch (err) {
    console.error('Error saving vehicles to Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Default invoices - empty array (no demo data)
export const DEFAULT_INVOICES = [];

// Load invoices from Firestore only - no localStorage fallback
export const getAllInvoices = async () => {
  try {
    if (!shouldUseFirestore()) {
      console.warn('User not authenticated, cannot load invoices from Firestore');
      return [];
    }
    
    const invoices = await invoicesService.getAll();
    // Remove Firestore metadata for compatibility
    return invoices.map(({ id, createdAt, updatedAt, ...invoice }) => invoice);
  } catch (err) {
    console.error('Error loading invoices from Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Synchronous version for backward compatibility (returns empty array - use async version)
export const getAllInvoicesSync = () => {
  // Always return empty array - use async getAllInvoices() for Firestore data
  return [];
};

// Get invoices (filtered to last year) from Firestore only
export const getInvoices = async () => {
  try {
    if (!shouldUseFirestore()) {
      console.warn('User not authenticated, cannot load invoices from Firestore');
      return [];
    }
    
    const invoices = await invoicesService.getRecent();
    // Ensure invoices are sorted by date descending (most recent first)
    return invoices.sort((a, b) => new Date(b.date) - new Date(a.date));
  } catch (err) {
    console.error('Error getting invoices from Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Synchronous version for backward compatibility (returns empty array - use async version)
export const getInvoicesSync = () => {
  // Always return empty array - use async getInvoices() for Firestore data
  return [];
};

// Save invoice to Firestore only - no localStorage fallback
export const saveInvoice = async (invoice) => {
  if (!shouldUseFirestore()) {
    throw new Error('User not authenticated, cannot save invoice to Firestore');
  }
  
  try {
    await invoicesService.add(invoice);
    // Clean up old invoices (older than 1 year)
    await invoicesService.cleanupOld();
  } catch (err) {
    console.error('Error saving invoice to Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Load garage contact info from Firestore only - no localStorage fallback
export const loadGarageContact = async () => {
  try {
    if (!shouldUseFirestore()) {
      console.warn('User not authenticated, cannot load garage contact from Firestore');
      return null;
    }
    
    const info = await garageInfoService.get();
    if (info) {
      return mapGarageInfoFromFirestore(info);
    }
    return null;
  } catch (err) {
    console.error('Error loading garage contact from Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Synchronous version for backward compatibility (returns null - use async version)
export const loadGarageContactSync = () => {
  // Always return null - use async loadGarageContact() for Firestore data
  return null;
};

// Save garage contact info to Firestore only - no localStorage fallback
export const saveGarageContact = async (contact) => {
  if (!shouldUseFirestore()) {
    throw new Error('User not authenticated, cannot save garage contact to Firestore');
  }
  
  try {
    const payload = {
      companyName: contact.companyName || '',
      address: contact.address || '',
      contact: contact.phone || contact.contact || '',
      vatNumber: contact.vatNumber || '',
      email: contact.email || '',
      siteNames: contact.siteNames || DEFAULT_GARAGE_INFO.siteNames,
      technicians: Array.isArray(contact.technicians) ? contact.technicians : [],
      defaultTechnician: contact.defaultTechnician || '',
      defaultSite: contact.defaultSite || '',
      whatsappServiceDueRemindersEnabled: contact.whatsappServiceDueRemindersEnabled === true,
      whatsappMotRemindersEnabled: contact.whatsappMotRemindersEnabled === true,
      enabledStages: normalizeEnabledStages(contact.enabledStages),
    };
    if (contact.jobCardTemplate != null) {
      payload.jobCardTemplate = contact.jobCardTemplate;
    }
    await garageInfoService.set(payload);
  } catch (err) {
    console.error('Error saving garage contact to Firestore:', err);
    throw err; // Throw error instead of falling back to localStorage
  }
};

// Generate invoice number
export const generateInvoiceNumber = async () => {
  const invoices = await getInvoices();
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const prefix = `INV-${year}${month}-`;
  const existingNums = invoices
    .filter(inv => inv.number && inv.number.startsWith(prefix))
    .map(inv => {
      const numPart = inv.number.replace(prefix, '');
      return parseInt(numPart, 10);
    })
    .filter(num => !isNaN(num));
  
  const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : 1;
  return `${prefix}${String(nextNum).padStart(4, '0')}`;
};

// Synchronous version for backward compatibility
export const generateInvoiceNumberSync = () => {
  const invoices = getInvoicesSync();
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const prefix = `INV-${year}${month}-`;
  const existingNums = invoices
    .filter(inv => inv.number && inv.number.startsWith(prefix))
    .map(inv => {
      const numPart = inv.number.replace(prefix, '');
      return parseInt(numPart, 10);
    })
    .filter(num => !isNaN(num));
  
  const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : 1;
  return `${prefix}${String(nextNum).padStart(4, '0')}`;
};

// Create invoice for a vehicle
export const createInvoiceForVehicle = async (veh, generateInvoiceNumberFn = generateInvoiceNumber) => {
  if (!veh) return null;
  
  const isMOTWorkshopBodyshopCheck = ['MOT', 'Workshop', 'Bodyshop', 'Tyres', 'In Progress'].includes(veh.status) ||
    (veh.previousStatus && ['MOT', 'Workshop', 'Bodyshop', 'Tyres'].includes(veh.previousStatus));
  
  // Check if invoice already exists for this vehicle
  const existingInvoicesRaw = await invoicesService.getAll();
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  const existingInvoiceRaw = existingInvoicesRaw.find(inv => inv.reg === veh.reg && inv.vehicle === veh.vehicle);
  if (existingInvoiceRaw) {
    const invDate = new Date(existingInvoiceRaw.date);
    if (invDate >= oneYearAgo) {
      // Invoice exists and is recent - update with latest itemised bill data if available
      const hasNewLineItems = isMOTWorkshopBodyshopCheck && (
        (Array.isArray(veh.lineItems) && veh.lineItems.length > 0) ||
        (typeof veh.labourCost === 'number' && veh.labourCost > 0)
      );
      const invoiceIsMissingItems = !existingInvoiceRaw.lineItems?.length && !existingInvoiceRaw.labourCost;
      if (hasNewLineItems && invoiceIsMissingItems && existingInvoiceRaw.id) {
        try {
          const updates = {
            lineItems: Array.isArray(veh.lineItems) ? veh.lineItems : [],
            labourCost: typeof veh.labourCost === 'number' ? veh.labourCost : 0
          };
          await invoicesService.update(existingInvoiceRaw.id, updates);
          const { createdAt, updatedAt, ...rest } = existingInvoiceRaw;
          return { ...rest, ...updates };
        } catch (err) {
          console.error('Error updating existing invoice with itemised data:', err);
        }
      }
      const { id, createdAt, updatedAt, ...existingInvoice } = existingInvoiceRaw;
      return existingInvoice;
    }
  }

  // Use the current date/time when the invoice is created
  // This ensures the invoice appears as the most recent in the invoices section
  const now = new Date();
  // Create ISO string with current date/time to ensure proper sorting
  // The normalizeToLocalMidnight function in Invoices.jsx will extract the date part correctly
  const invoiceDate = now.toISOString();
  
  const invoiceNum = await generateInvoiceNumberFn();
  const subtotal = veh.cost || 0;
  // For MOT/Workshop/Bodyshop/Tyres, calculate VAT based on includeVat field (MOT = no VAT)
  const isMOTWorkshopBodyshop = ['MOT', 'Workshop', 'Bodyshop', 'Tyres'].includes(veh.status);
  const vatEnabled = isMOTWorkshopBodyshop ? (veh.status === 'MOT' ? false : (veh.includeVat !== undefined ? veh.includeVat : true)) : true;
  const vatAmount = vatEnabled ? subtotal * 0.2 : 0;
  const total = subtotal + vatAmount;

  const dropOffDate = veh.dropOffDate 
    ? (formatDateOnlyDisplay(veh.dropOffDate, { day: 'numeric', month: 'long', year: 'numeric' }) || 'Not set')
    : 'Not set';
  const collectionDate = veh.collectionDate 
    ? (formatDateOnlyDisplay(veh.collectionDate, { day: 'numeric', month: 'long', year: 'numeric' }) || 'Not set')
    : 'Not set';

  const invoice = {
    number: invoiceNum,
    date: invoiceDate,
    reg: veh.reg,
    vehicle: veh.vehicle,
    contact: veh.contact || '',
    summary: veh.summary || '',
    motStatus: veh.motStatus || '',
    dropOffDate: dropOffDate,
    collectionDate: collectionDate,
    subtotal: subtotal,
    vat: vatAmount,
    total: total,
    // Include estimated cost for MOT/Workshop/Bodyshop vehicles
    ...(isMOTWorkshopBodyshop && veh.estimatedCost !== undefined && {
      estimatedCost: veh.estimatedCost
    }),
    // Itemised bill: line items and labour cost for Workshop/Bodyshop/MOT (so invoice can show breakdown)
    ...(isMOTWorkshopBodyshop && {
      lineItems: Array.isArray(veh.lineItems) ? veh.lineItems : [],
      labourCost: typeof veh.labourCost === 'number' ? veh.labourCost : 0
    })
  };
  
  try {
    await saveInvoice(invoice);
    console.log('Invoice saved successfully:', invoice.number);
  } catch (error) {
    console.error('Error saving invoice:', error);
    // Still return the invoice even if save fails, so it appears in UI
  }
  
  return invoice;
};

